import { useEffect, useRef } from 'react';
import { FiX } from 'react-icons/fi';
import useRecoveryHelp from '../../../../hooks/useRecoveryHelp';
import type { HelpTopicKey } from '../../../../types/LegalCase';

interface HelpPanelProps {
    open: boolean;
    topic: HelpTopicKey;
    onClose: () => void;
    // Quando o painel abre de dentro de um modal (Dialog usa z-40/z-50), sobe uma camada para
    // ficar acima dele. Default é o mesmo z do FilterPanel.
    elevated?: boolean;
}

const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Prende o Tab dentro do painel (o `aria-modal` promete isso ao leitor de tela). Do último elemento
// o Tab volta ao primeiro, e do primeiro o Shift+Tab vai ao último. Fora do componente (regra do
// AGENTS.md: sem função dentro de função).
const trapTab = (e: KeyboardEvent, container: HTMLElement | null): void => {
    if (!container) return;
    const items = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    const outside = !container.contains(active);
    if (e.shiftKey && (active === first || outside)) {
        e.preventDefault();
        last.focus();
        return;
    }
    if (!e.shiftKey && (active === last || outside)) {
        e.preventDefault();
        first.focus();
    }
};

// Drawer lateral direito com a ajuda contextual da seção ("manual vivo" — Task 21). Reusa o
// padrão de overlay/foco/Escape do FilterPanel (que veio do SidePanelDrawer do b3-custody):
// backdrop clicável, role=dialog + aria-modal, foco no botão Fechar ao abrir, Escape via ref
// para não roubar o foco a cada re-render do pai. Darkmode. Conteúdo em três partes fixas.
const HelpPanel = ({ open, topic, onClose, elevated = false }: HelpPanelProps) => {
    const content = useRecoveryHelp(topic);
    const closeButtonRef = useRef<HTMLButtonElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    // `onClose` chega inline do pai (recriado a cada render); guardamos a versão mais recente num
    // ref para o listener de Escape não entrar nas deps do efeito — senão o efeito re-roda a cada
    // render e rouba o foco de volta para o botão Fechar. Armadilha já apontada pelo Copilot no
    // FilterPanel.
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    // Ao abrir: guarda quem tinha o foco (o botão de ajuda), foca o Fechar e prende o Tab no painel.
    // Ao fechar: devolve o foco a quem o tinha — senão ele fica no botão Fechar que acabou de sumir e
    // o teclado recomeça do topo da página (achado do Copilot na PR #1215).
    useEffect(() => {
        if (!open) return;
        const previous = document.activeElement as HTMLElement | null;
        closeButtonRef.current?.focus();
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onCloseRef.current();
            if (e.key === 'Tab') trapTab(e, panelRef.current);
        };
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('keydown', onKeyDown);
            if (previous && document.contains(previous)) previous.focus();
        };
    }, [open]);

    if (!open || !content) return null;

    const backdropZ = elevated ? 'z-[60]' : 'z-40';
    const panelZ = elevated ? 'z-[60]' : 'z-50';

    return (
        <>
            <div className={`fixed inset-0 bg-black bg-opacity-40 ${backdropZ}`} onClick={onClose} />
            <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-label={`Ajuda: ${content.title}`}
                className={`fixed right-0 top-0 h-full w-full max-w-sm bg-white dark:bg-slate-800 border-l border-gray-200 dark:border-slate-700 shadow-xl ${panelZ} flex flex-col`}
            >
                <div className="flex-shrink-0 flex items-center justify-between p-4 border-b border-gray-200 dark:border-slate-700">
                    <h2 className="text-base font-semibold text-gray-900 dark:text-white">{content.title}</h2>
                    <button
                        type="button"
                        ref={closeButtonRef}
                        onClick={onClose}
                        aria-label="Fechar ajuda"
                        className="p-1.5 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg"
                    >
                        <FiX size={18} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-5">
                    <p className="text-sm text-gray-600 dark:text-gray-300">{content.problem}</p>

                    <section className="space-y-2">
                        <h3 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Como usar</h3>
                        <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700 dark:text-gray-200">
                            {content.howTo.map((step) => (
                                <li key={step}>{step}</li>
                            ))}
                        </ul>
                    </section>

                    <section className="space-y-2">
                        <h3 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">O que isto impacta</h3>
                        <ul className="list-disc pl-5 space-y-1.5 text-sm text-gray-700 dark:text-gray-200">
                            {content.impacts.map((impact) => (
                                <li key={impact}>{impact}</li>
                            ))}
                        </ul>
                    </section>
                </div>
            </div>
        </>
    );
};

export default HelpPanel;
